import {useState} from "react";
import {LoginType, SubscriptionTier} from "./types";

export const useAuth = () => {

  const [token, setToken] = useState<string | null>(localStorage.getItem("access_token") || null);
  const [user, setUser] = useState<string | null>(null);
  const [loginType, setLoginType] = useState<LoginType | undefined>(undefined);
  const loginPersistenceHours = 2;

  const logout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("login_type");
    localStorage.removeItem("access_token_expires");
    localStorage.removeItem("current_user")
  }

  const storeAccessToken = (access_token: string | null) => {
    console.debug("Called storeAccessToken: ", access_token)
    if (access_token === null) {
      localStorage.removeItem("access_token");
      return;
    }
    localStorage.setItem("access_token", access_token);
    const twoHoursFromNowUTC = new Date(Date.now() + loginPersistenceHours * 60 * 60 * 1000).toISOString();
    // for testing
    // const thirtySecondsFromNowUTC = new Date(Date.now() + 30 * 1000).toISOString();
    localStorage.setItem("access_token_expires", twoHoursFromNowUTC);
    setToken(access_token);
    console.debug("Stored access token")
  }

  const storeLoginType = (loginType: LoginType) => {
    setLoginType(loginType);
    localStorage.setItem("login_type", loginType);
  }


  const readAccessToken = () => {
    console.debug("Read access token")
    const storedToken = localStorage.getItem("access_token");
    const storedTokenExpirationUTC = localStorage.getItem("access_token_expires");
    if (storedTokenExpirationUTC !== null) {
      const expiresAt = Date.parse(storedTokenExpirationUTC);
      const isExpired = Date.now() > expiresAt;
      if (isExpired) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("access_token_expires");
        return null;
      }
    }

    setToken(storedToken);
    return storedToken;
  }

  const setCurrentUser = (providedUser: string | null) => {
    if (providedUser) {
      setUser(providedUser);
    }
  }


  const getPayloadFromAccessToken = (accessToken: string): any => {
    try {
      // JWT tokens have three parts separated by dots
      const parts = accessToken.split('.');
      if (parts.length !== 3) {
        return null;
      }

      // Decode the payload (second part)
      return JSON.parse(atob(parts[1]));
    } catch (error) {
      console.error("Failed to decode access token:", error);
      return null;
    }

  }

  const getCurrentUserFromAccessToken = (): string | null => {
    const storedToken = localStorage.getItem("access_token");
    if (!storedToken) {
      return null;
    }

    try {
      const payload = getPayloadFromAccessToken(storedToken);
      const username = payload.username;

      if (username) {
        setUser(username);
        return username;
      }

      return null;
    } catch (error) {
      console.error("Failed to decode access token:", error);
      return null;
    }
  }

  const getCurrentUser = () => {
    if (!user) {
      const currentUser= localStorage.getItem("current_user")
      if (!currentUser) {
        const currentUserFromAccessToken = getCurrentUserFromAccessToken();
        setUser(currentUserFromAccessToken);
        return currentUserFromAccessToken;
      }
      setUser(currentUser);
      return currentUser;
    }
    return user;
  }

  const isAuthenticated = () => {
    return token !== null;
  }

  const getLoginType = () => {
    const _loginType = localStorage.getItem("login_type");
    if (_loginType) {
      setLoginType(_loginType as LoginType);
    }
    return _loginType;
  }


  const getSubscriptionTier = (): string | null => {
    const storedToken = localStorage.getItem("access_token");
    if (!storedToken) {
      return null;
    }

    try {
      // Decode the payload (second part)
      const payload = getPayloadFromAccessToken(storedToken)
      const tier = payload.tier;

      if (tier) {
        return tier as SubscriptionTier;
      }

      console.error("Failed to extract subscription tier from access token")
      return SubscriptionTier.free;
    } catch (error) {
      console.error("Failed to decode access token:", error);
      return SubscriptionTier.free;
    }
  }


  return {
    storeAccessToken,
    readAccessToken,
    setCurrentUser,
    getCurrentUser,
    isAuthenticated,
    getLoginType,
    storeLoginType,
    logout,
    getSubscriptionTier
  };


}